<script lang="ts">
const DISMISSED_STATUS_LS_KEY = 'zephyr.dismissedStatus';
const DISMISSED_ANNOUNCEMENT_LS_KEY = 'zephyr.dismissedAnnouncement';

export default { name: 'StatusAlertBanner' };
</script>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';

import { api } from '@api';
import { ls } from '@services/ls';

import Icon, { type IconDefinition } from '@components/Icon.vue';

import { faExclamationTriangle } from '@icons/solid/faExclamationTriangle';
import { faMegaphone } from '@icons/solid/faMegaphone';

export interface FixedPositionInfo {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

interface AlertInfo {
  id: string;
  text: string;
  link: string | null;
}

const loadingComplete = ref(false);

const statusData = ref<AlertInfo | null>(null);
const announcementData = ref<AlertInfo | null>(null);

interface BannerInfo {
  type: 'status' | 'announcement';
  id: string;
  text: string;
  link: string | null;
  icon: IconDefinition;
}

const banners = computed(() => {
  const items: BannerInfo[] = [];

  if (statusData.value) {
    items.push({
      type: 'status',
      id: statusData.value.id,
      text: statusData.value.text,
      link: statusData.value.link,
      icon: faExclamationTriangle,
    });
  }

  if (announcementData.value) {
    items.push({
      type: 'announcement',
      id: announcementData.value.id,
      text: announcementData.value.text,
      link: announcementData.value.link,
      icon: faMegaphone,
    });
  }

  return items;
});

const showBanners = computed(() => {
  return !!loadingComplete.value && banners.value.length > 0;
});

onMounted(() => {
  void getAlertData();
});

async function getStatusData() {
  const statusItems = await api.siteStatus.getStatus();

  if (statusItems.length === 0) {
    return;
  }

  // Get the most recent status item.
  const status = statusItems.sort((a, b) => {
    return new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime();
  })[0] as api.siteStatus.SiteStatusItem;

  const date = new Date(status.pubDate);

  // Don't show if the status is older than 24 hours.
  if (Date.now() - date.getTime() > 24 * 60 * 60 * 1000) {
    return;
  }

  // console.log(
  //   'time (in hours) since latest status update: ',
  //   (Date.now() - date.getTime()) / (1000 * 60 * 60),
  // );

  const id = status.guid.replace(status.link, '');

  // Don't show if dismissed
  if (id === getDismissedAlert(DISMISSED_STATUS_LS_KEY)) {
    return;
  }

  const text = status.description;

  let link: string | null;

  // Don't include the link if it doesn't link to a specific  status/incident
  // page.
  if (status.link === 'https://status.zephyr-sim.com/') {
    link = null;
  } else {
    link = status.link;
  }

  statusData.value = {
    id,
    text,
    link,
  };
}

async function getAnnouncementData() {
  const data = await api.siteStatus.getAnnouncement();

  if (data.hidden || !data.digest || !data.link || !data.html) {
    return;
  }

  // Don't show if dismissed
  if (data.digest === getDismissedAlert(DISMISSED_ANNOUNCEMENT_LS_KEY)) {
    return;
  }

  const tempNode = document.createElement('div');
  tempNode.innerHTML = data.html;

  const announcementText = tempNode.textContent?.trim() ?? '';
  tempNode.remove();

  announcementData.value = {
    id: data.digest,
    text: announcementText,
    link: data.link,
  };
}

async function getAlertData() {
  await getAnnouncementData();
  await getStatusData();

  loadingComplete.value = true;

  // console.log({
  //   statusData: statusData.value,
  //   announcementData: announcementData.value,
  // });
}

//#region Helper Functions

function getDismissedAlert(key: string) {
  return ls.get(key) ?? null;
}

function openLink(link: string) {
  window.open(link, '_blank');
}

function dismissBanner(id: string) {
  if (id === statusData.value?.id) {
    ls.set(DISMISSED_STATUS_LS_KEY, id);

    statusData.value = null;
  } else if (id === announcementData.value?.id) {
    ls.set(DISMISSED_ANNOUNCEMENT_LS_KEY, id);

    announcementData.value = null;
  }
}

//#endregion Helper Functions
</script>

<template>
  <div v-if="showBanners" class="banner-container">
    <div
      v-for="banner in banners"
      :key="banner.id"
      :class="[
        'alert-banner',
        banner.type === 'status' ? 'status-alert' : 'announcement-alert',
      ]"
    >
      <div class="banner-content">
        <div class="alert-icon">
          <Icon :icon="banner.icon" />
        </div>

        <div class="alert-text" v-html="banner.text" />

        <div class="alert-buttons">
          <div v-if="banner.link" class="alert-button-container">
            <button @click.stop="openLink(banner.link)">Info</button>
          </div>

          <div v-if="banner.link" class="alert-button-container">
            <button @click.stop="dismissBanner(banner.id)">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.banner-container {
  position: relative;
  z-index: 99999;
}

.alert-banner {
  width: 100vw;
  color: #ffffffcf;
  overflow: hidden;

  &.status-alert {
    background-color: #ff6800;
  }

  &.announcement-alert {
    background-color: #ff4900;
  }
}

.banner-content {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 40px;
  max-width: 1400px;
  margin: 0 auto;
  gap: 40px;
  text-align: left;

  @include app-mobile {
    flex-direction: column;
    text-align: center;
    gap: 15px;
    padding: 20px;
  }
}

.alert-icon {
  font-size: 2rem;

  @include app-mobile {
    display: none;
  }
}

.alert-text {
  flex: 1;

  @include app-mobile {
    line-height: 1.3em;
  }
}

.alert-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}

.alert-button-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  > button {
    width: 45px;
    color: #ffffffcf;
    line-height: 1em;
    border: 1px solid #ffffffcf;
    padding: 3px 5px;
    border-radius: 5px;
    font-size: 0.8em;
    background-color: transparent;

    &:hover {
      opacity: 0.8;
      cursor: pointer;
      background-color: #ffffffcf;

      .status-alert & {
        color: #ff6800;
      }

      .announcement-alert & {
        color: #ff4900;
      }
    }
  }
}
</style>
